import {
  AccountInfo,
  InteractionStatus,
  IPublicClientApplication,
  Logger,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';

export interface AuthState {
  instance: IPublicClientApplication;
  account: AccountInfo | null;
  inProgress: InteractionStatus;
  logger: Logger;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  userId: string;
  roles: Roles[];
}

export type Roles = 'admin' | 'editor' | 'viewer' | 'No roles assigned';

function extractRoles(account: AccountInfo | null): Roles[] {
  const userRoles = (account?.idTokenClaims?.roles as string[]) || [];
  const userGroups = (account?.idTokenClaims?.groups as string[]) || [];

  const extractedRoles =
    userRoles.length > 0
      ? userRoles
      : userGroups.map(group => group.toLowerCase());

  return (
    extractedRoles.length > 0 ? extractedRoles : ['No roles assigned']
  ) as Roles[];
}

export default function useAuth(): AuthState {
  const { instance, accounts, inProgress, logger } = useMsal();
  const [account, setActiveAccount] = useState<AccountInfo | null>(null);
  const [roles, setRoles] = useState<Roles[]>([]);
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [userId, setUserId] = useState('Unknown User');

  useEffect(() => {
    function getAccount() {
      return (
        instance.getActiveAccount() ||
        (accounts && accounts.length > 0 ? accounts[0] : null)
      );
    }

    const currentAccount = getAccount();
    const currentRoles = extractRoles(currentAccount);

    setActiveAccount(currentAccount);
    setRoles(currentRoles);
    setAuthenticated(currentAccount !== null);
    setIsAdmin(currentRoles.includes('admin'));
    setIsEditor(currentRoles.includes('editor'));
    setIsViewer(currentRoles.includes('viewer'));
    setUserId(
      currentAccount?.username ||
        currentAccount?.name ||
        currentAccount?.idTokenClaims?.preferred_username ||
        'Unknown User'
    );
  }, [instance, accounts]);

  return {
    instance,
    account,
    inProgress,
    logger,
    isAuthenticated,
    isAdmin,
    isEditor,
    isViewer,
    userId,
    roles,
  };
}
